export interface ContextPaneView {
  id: string;
  title: string;
  sourceLabel: string;
  body: HTMLElement;
  actions?: HTMLElement;
  trigger?: HTMLElement;
}

export interface ContextPane {
  element: HTMLElement;
  push(view: ContextPaneView): void;
  replace(view: ContextPaneView): void;
  pop(): void;
  close(): void;
  destroy(): void;
  readonly isOpen: boolean;
}

interface ContextPaneOptions {
  onOpenChange?: (isOpen: boolean) => void;
}

const DESKTOP_BREAKPOINT = 1024;
const CLOSE_TRANSITION_MS = 240;
let paneCount = 0;

function focusableElements(root: HTMLElement): HTMLElement[] {
  return [
    ...root.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ].filter(
    (element) =>
      !element.hasAttribute('hidden') &&
      element.getAttribute('aria-hidden') !== 'true',
  );
}

export function createContextPane(
  options: ContextPaneOptions = {},
): ContextPane {
  const layer = document.createElement('div');
  layer.className = 'context-layer';
  layer.hidden = true;

  const backdrop = document.createElement('button');
  backdrop.className = 'context-backdrop';
  backdrop.type = 'button';
  backdrop.tabIndex = -1;
  backdrop.setAttribute('aria-label', 'Close context');

  const panel = document.createElement('section');
  panel.className = 'context-pane';
  panel.tabIndex = -1;

  const header = document.createElement('header');
  header.className = 'context-pane__header';

  const backButton = document.createElement('button');
  backButton.className = 'icon-button context-pane__back';
  backButton.type = 'button';
  backButton.setAttribute('aria-label', 'Back');
  backButton.textContent = '←';
  backButton.hidden = true;

  const headingCopy = document.createElement('div');
  headingCopy.className = 'context-pane__heading';
  const source = document.createElement('p');
  source.className = 'context-pane__source';
  const title = document.createElement('h2');
  paneCount += 1;
  title.id = `context-pane-title-${String(paneCount)}`;
  panel.setAttribute('aria-labelledby', title.id);
  headingCopy.append(source, title);

  const headerTools = document.createElement('div');
  headerTools.className = 'context-pane__header-tools';
  const actionsSlot = document.createElement('div');
  actionsSlot.className = 'context-pane__actions';
  const closeButton = document.createElement('button');
  closeButton.className = 'icon-button context-pane__close';
  closeButton.type = 'button';
  closeButton.setAttribute('aria-label', 'Close context');
  closeButton.textContent = '×';
  headerTools.append(actionsSlot, closeButton);
  header.append(backButton, headingCopy, headerTools);

  const bodySlot = document.createElement('div');
  bodySlot.className = 'context-pane__body';
  panel.append(header, bodySlot);
  layer.append(backdrop, panel);

  const views: ContextPaneView[] = [];
  let open = false;
  let destroyed = false;
  let mobile = window.innerWidth < DESKTOP_BREAKPOINT;
  let returnFocus: HTMLElement | null = null;
  let previousBodyOverflow = '';
  let closeTimer = 0;
  let openFrame = 0;

  const setScrollLock = (shouldLock: boolean): void => {
    if (shouldLock) {
      previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = previousBodyOverflow;
    }
  };

  const syncMode = (): void => {
    const nextMobile = window.innerWidth < DESKTOP_BREAKPOINT;
    if (mobile !== nextMobile && open) {
      setScrollLock(nextMobile);
    }
    mobile = nextMobile;
    if (mobile) {
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
    } else {
      panel.setAttribute('role', 'complementary');
      panel.removeAttribute('aria-modal');
    }
  };

  const focusHeaderControl = (): void => {
    (views.length > 1 ? backButton : closeButton).focus({
      preventScroll: true,
    });
  };

  const render = (): void => {
    const view = views.at(-1);
    if (!view) return;
    source.textContent = view.sourceLabel;
    title.textContent = view.title;
    backButton.hidden = views.length <= 1;
    backButton.setAttribute(
      'aria-label',
      views.length > 1
        ? `Back to ${views.at(-2)?.title ?? 'previous context'}`
        : 'Back',
    );
    actionsSlot.replaceChildren(...(view.actions ? [view.actions] : []));
    actionsSlot.hidden = !view.actions;
    bodySlot.replaceChildren(view.body);
  };

  const show = (): void => {
    window.clearTimeout(closeTimer);
    window.cancelAnimationFrame(openFrame);
    syncMode();
    layer.hidden = false;
    layer.removeAttribute('aria-hidden');
    layer.classList.remove('context-layer--open');
    openFrame = window.requestAnimationFrame(() => {
      layer.classList.add('context-layer--open');
    });
    if (mobile) setScrollLock(true);
    options.onOpenChange?.(true);
    focusHeaderControl();
  };

  const close = (): void => {
    if (!open) return;
    open = false;
    views.length = 0;
    backButton.hidden = true;
    layer.classList.remove('context-layer--open');
    layer.setAttribute('aria-hidden', 'true');
    if (mobile) setScrollLock(false);
    options.onOpenChange?.(false);
    closeTimer = window.setTimeout(() => {
      layer.hidden = true;
      bodySlot.replaceChildren();
      actionsSlot.replaceChildren();
    }, CLOSE_TRANSITION_MS);
    const focusTarget = returnFocus;
    returnFocus = null;
    focusTarget?.focus({ preventScroll: true });
  };

  const pop = (): void => {
    if (!open) return;
    if (views.length <= 1) {
      close();
      return;
    }
    views.pop();
    render();
    focusHeaderControl();
  };

  const handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (views.length > 1) pop();
      else close();
      return;
    }
    if (!mobile || event.key !== 'Tab') return;
    const focusable = focusableElements(panel);
    if (focusable.length === 0) {
      event.preventDefault();
      panel.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleResize = (): void => syncMode();

  backButton.addEventListener('click', pop);
  closeButton.addEventListener('click', close);
  backdrop.addEventListener('click', close);
  panel.addEventListener('keydown', handleKeyDown);
  window.addEventListener('resize', handleResize);
  syncMode();

  return {
    element: layer,
    push(view) {
      if (destroyed) return;
      if (!open) {
        returnFocus =
          view.trigger ??
          (document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null);
        views.length = 0;
        open = true;
      }
      views.push(view);
      render();
      if (views.length === 1) show();
      else focusHeaderControl();
    },
    replace(view) {
      if (destroyed) return;
      returnFocus =
        view.trigger ??
        (document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null);
      views.splice(0, views.length, view);
      render();
      if (!open) {
        open = true;
        show();
      } else {
        focusHeaderControl();
      }
    },
    pop,
    close,
    destroy() {
      if (destroyed) return;
      destroyed = true;
      window.clearTimeout(closeTimer);
      window.cancelAnimationFrame(openFrame);
      window.removeEventListener('resize', handleResize);
      if (open && mobile) setScrollLock(false);
      open = false;
      views.length = 0;
      layer.remove();
    },
    get isOpen() {
      return open;
    },
  };
}
